// /src/stores/index.ts
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { viewPrefSyncMiddleware } from "./middleware/viewPrefSyncMiddleware";
import {
  persistStore,
  persistReducer,
  createTransform,
  createMigrate,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import authReducer from "./slices/authSlice";
import rbacReducer from "@/rbac/rbacSlice";
import stageReducer from "../features/stage/stores/stageFucntionalSlice";
import addStageSlice from "../features/stage/stores/addStageSlice";
import editStageSlice from "../features/stage/stores/editStageSlice";
import userStageSlice from "@/features/users/stores/userFuntionalSlice";
import branchSlice from "@/features/users/stores/branchFunctionalSlice";
import leadCardFunctionalityReducer from "@/features/leads/stores/leadCardFunctionalitySlice";
import viewLeadDisplayReducer from "@/features/leads/stores/viewLeadDisplaySlice";
import leadFunctionalReducer from "@/features/leads/stores/leadFunctionalSlice";
import leadFilterSlice from "@/features/leads/stores/leadFilterSlice";
import sourceSlice from "@/features/sourceAndCampaign/stores/leadSourceFunctional";
import customSlice from "@/features/customField/stores/customInputFunctionalSlice";
import universityFunctionalReducer from "../features/universityAndCourse/stores/universityFunctionalSlice";
import universityFilterReducer from "../features/universityAndCourse/stores/universityFilterSlice";
import courseFunctionalReducer from "../features/universityAndCourse/stores/courseFunctionalSlice";
import courseFilterReducer from "../features/universityAndCourse/stores/courseFilterSlice";
import scholarshipFunctionalReducer from "../features/universityAndCourse/stores/scholarshipFunctionalSlice";
import dynamicCardFunctionalityReducer from "../features/dynamic/store/DynamicCardFunctionalitySlice";
import dynamicFunctionalReducer, {
  defaultRouteState as dynamicDefaultRouteState,
  type DynamicFunctionalState,
  type DynamicViewTab,
} from "../features/dynamic/store/dynamicFunctionalSlice";
import dynamicFilterReducer from "../features/dynamic/store/dynamicFilterSlice";
import roleFunctionalSlice from "../features/users/stores/roleFunctionalSlice";
import settingsReducer from "../features/settings/stores/settingsSlice";
import automationFunctionalReducer from "../features/automation/stores/automationFunctionalSlice";
import archievedLeadCardSlice from "../features/leads/stores/ArchievedLeadCardSlice";
import duplicateLeadCardSlice from "../features/leads/stores/DuplicateLeadCardSlice";
import branchMismatchLeadCardSlice from "../features/leads/stores/BranchMismatchLeadCardSlice";
import educationSlice from "@/module/education/stores/educationSlice";
import childFuncionalReducer from "@/features/leads/stores/childSlice";
import videoProductionFunctionalReducer from "@/features/videoProduction/stores/videoProductionFunctionalSlice";
import academyFunctionalReducer from "@/features/academy/stores/academyFunctionalSlice";
// import productsFunctionalReducer from "@/features/products/stores/productsFunctionalSlice";
// import catalogueFunctionalReducer from "@/features/catalogue/stores/catalogueFunctionalSlice";
import followUpNotificationReducer from "@/features/leads/stores/followUpNotificationSlice";
import installmentNotificationReducer from "@/features/leads/stores/installmentNotificationSlice";
import studentPortalReducer, {
  type StudentPortalState,
} from "@/features/studentPortal/stores/studentPortalSlice";
import whatsappReducer from "@/features/whatsapp/stores/whatsappSlice";
import gmailReducer from "@/features/gmail/stores/gmailSlice";
import callLogReducer from "@/stores/slices/callLogSlice";
import managePreferencesReducer from "@/features/settings/stores/managePreferencesSlice";
import tracksManagementReducer from "@/features/stage/stores/tracksManagementSlice";
import requirementFunctionalReducer from "@/features/stage/stores/requirementFunctionalSlice";
import notificationReducer from "@/features/notifications/stores/notificationSlice";
import aiChatReducer from "@/features/ai/stores/aiChatSlice";
import userListFilterReducer from "@/features/users/stores/userListFilterSlice";
import roleListFilterReducer from "@/features/users/stores/roleListFilterSlice";
import branchListFilterReducer from "@/features/users/stores/branchListFilterSlice";
import dashboardV2Reducer from "@/features/Dashboard/v2/state/dashboardV2Slice";
import reportsV2Reducer from "@/features/report/v2/state/reportsV2Slice";
import uiDensityReducer from "@/features/settings/stores/uiDensitySlice";

/**
 * Lead-card preference rename: the legacy `isCourseShown` key was used to
 * drive the follow-up / deadline row on cards (and the "Course" column in
 * the table). It has been renamed to `isFollowUpShown` to match the actual
 * UI semantics. We also introduce a new `isPaymentStatusShown` toggle.
 *
 * This migration carries forward whatever value users had under the old
 * key so existing preferences aren't silently reset.
 */
const leadCardMigrations = {
  1: (state: any) => {
    if (!state) return state;
    const next = { ...state };
    if ("isCourseShown" in next && next.isFollowUpShown === undefined) {
      next.isFollowUpShown = Boolean(next.isCourseShown);
    }
    if (next.isPaymentStatusShown === undefined) {
      next.isPaymentStatusShown = false;
    }
    delete next.isCourseShown;
    return next;
  },
  /**
   * v2: introduces per-card show/hide for tenant custom fields and niche
   * modules. The slice stores two id→bool maps; default to empty so
   * existing users don't suddenly see new columns until they pick them.
   */
  2: (state: any) => {
    if (!state) return state;
    const next = { ...state };
    if (
      !next.customFieldToggles ||
      typeof next.customFieldToggles !== "object"
    ) {
      next.customFieldToggles = {};
    }
    if (!next.moduleToggles || typeof next.moduleToggles !== "object") {
      next.moduleToggles = {};
    }
    return next;
  },
  /**
   * v3: belt-and-suspenders shape repair. A handful of users reported the
   * Show/Hide menu "not persisting" — the most likely cause was a
   * partially-written blob from an older slice version (missing some
   * boolean keys) leaving rehydrate with `undefined` fields that the
   * dropdown then treated as unchecked. We force every required key to
   * exist with a sensible default, preserving any value the user had
   * actually set. Anything in storage that isn't part of the current
   * shape is dropped.
   */
  3: (state: any) => {
    if (!state) return state;
    const defaults: Record<string, any> = {
      isNameShown: true,
      isMobileNumberShown: true,
      isBranchShown: true,
      isUsersShown: true,
      isStageShown: true,
      isStatusShown: true,
      isEmailShown: true,
      isFollowUpShown: true,
      isLeadSourceShown: true,
      isCampaignShown: true,
      isSubstatusShown: true,
      isPriorityShown: true,
      isLastUpdatedOnShown: true,
      isRemarksShown: true,
      isPaymentStatusShown: false,
      isLastCallRecordShown: false,
      isLeadScoreShown: false,
      customFieldToggles: {},
      moduleToggles: {},
    };
    const next: Record<string, any> = {};
    for (const k of Object.keys(defaults)) {
      const v = state[k];
      if (k === "customFieldToggles" || k === "moduleToggles") {
        next[k] = v && typeof v === "object" && !Array.isArray(v) ? v : {};
      } else {
        next[k] = typeof v === "boolean" ? v : defaults[k];
      }
    }
    return next;
  },
  /**
   * v4: introduces the per-card "show a role's assignee" map. Default to
   * empty so existing users don't suddenly see new columns, and so the
   * dropdown treats every role as unchecked until picked. Carries the
   * previous shape through untouched.
   */
  4: (state: any) => {
    if (!state) return state;
    const next = { ...state };
    if (!next.roleToggles || typeof next.roleToggles !== "object") {
      next.roleToggles = {};
    }
    return next;
  },
};

const dynamicCardMigrations = {
  1: (state: any) => {
    if (!state || typeof state !== "object") return state;
    const next: Record<string, any> = {};
    for (const [routeId, route] of Object.entries(state)) {
      if (!route || typeof route !== "object") {
        next[routeId] = route;
        continue;
      }
      const r: any = { ...(route as Record<string, unknown>) };
      if ("isCourseShown" in r && r.isFollowUpShown === undefined) {
        r.isFollowUpShown = Boolean(r.isCourseShown);
      }
      if (r.isPaymentStatusShown === undefined) {
        r.isPaymentStatusShown = false;
      }
      delete r.isCourseShown;
      next[routeId] = r;
    }
    return next;
  },
  /**
   * v2: backfill the per-route `roleToggles` map (role-assignee columns).
   * Default empty so existing dynamic tabs don't sprout new columns until
   * the user picks a role. Also repairs the CF/module maps if a stale blob
   * is missing them.
   */
  2: (state: any) => {
    if (!state || typeof state !== "object") return state;
    const next: Record<string, any> = {};
    for (const [routeId, route] of Object.entries(state)) {
      if (!route || typeof route !== "object") {
        next[routeId] = route;
        continue;
      }
      const r: any = { ...(route as Record<string, unknown>) };
      if (!r.customFieldToggles || typeof r.customFieldToggles !== "object") {
        r.customFieldToggles = {};
      }
      if (!r.moduleToggles || typeof r.moduleToggles !== "object") {
        r.moduleToggles = {};
      }
      if (!r.roleToggles || typeof r.roleToggles !== "object") {
        r.roleToggles = {};
      }
      next[routeId] = r;
    }
    return next;
  },
};

const leadCardPersistConfig = {
  key: "leadCardFunctionality",
  storage,
  version: 4,
  migrate: createMigrate(leadCardMigrations, { debug: false }),
};

const dynamicCardPersistConfig = {
  key: "dynamicCardFunctionality",
  storage,
  version: 2,
  migrate: createMigrate(dynamicCardMigrations, { debug: false }),
};

const persistedLeadCardFunctionalityReducer = persistReducer(
  leadCardPersistConfig,
  leadCardFunctionalityReducer
);

const persistedDynamicCardFunctionalityReducer = persistReducer(
  dynamicCardPersistConfig,
  dynamicCardFunctionalityReducer
);

const managePreferencesPersistConfig = {
  key: "managePreferences",
  storage,
};

const persistedManagePreferencesReducer = persistReducer(
  managePreferencesPersistConfig,
  managePreferencesReducer
);

const viewLeadDisplayPersistConfig = {
  key: "viewLeadDisplay",
  storage,
};

const persistedViewLeadDisplayReducer = persistReducer(
  viewLeadDisplayPersistConfig,
  viewLeadDisplayReducer
);

const leadFunctionalPersistConfig = {
  key: "leadFunctional",
  storage,
  // `isListFullscreen` rides along with the view tab: full screen is a MODE
  // the user chose ("this is how I work the list"), not a transient panel, so
  // a refresh — or a bounce out to the dashboard and back — must not silently
  // drop them into the boxed view. Exiting writes `false` the same way.
  whitelist: ["currentViewTab", "isListFullscreen"],
};

const persistedLeadFunctionalReducer = persistReducer(
  leadFunctionalPersistConfig,
  leadFunctionalReducer
);

const dynamicFunctionalViewTransform = createTransform(
  (inboundState: DynamicFunctionalState) => {
    const slim: Record<string, { currentViewTab: DynamicViewTab }> = {};
    for (const [id, route] of Object.entries(inboundState ?? {})) {
      if (route?.currentViewTab) {
        slim[id] = { currentViewTab: route.currentViewTab };
      }
    }
    return slim;
  },
  (
    outboundState:
      | Record<string, { currentViewTab: DynamicViewTab }>
      | undefined
  ) => {
    const merged: DynamicFunctionalState = {};
    for (const [id, v] of Object.entries(outboundState ?? {})) {
      merged[id] = { ...dynamicDefaultRouteState, ...v };
    }
    return merged;
  }
);

const dynamicFunctionalPersistConfig = {
  key: "dynamicFunctional",
  storage,
  transforms: [dynamicFunctionalViewTransform],
};

const persistedDynamicFunctionalReducer = persistReducer(
  dynamicFunctionalPersistConfig,
  dynamicFunctionalReducer
);

const appReducer = combineReducers({
    auth: authReducer,
    rbac: rbacReducer,
    stageFunctional: stageReducer,
    addstage: addStageSlice,
    editStage: editStageSlice,
    userFunctional: userStageSlice,
    roleFunctional: roleFunctionalSlice,
    branchFunctionality: branchSlice,
    userListFilter: userListFilterReducer,
    roleListFilter: roleListFilterReducer,
    branchListFilter: branchListFilterReducer,
    sourceFunctionality: sourceSlice,
    customFuncationality: customSlice,
    leadCardFunctionality: persistedLeadCardFunctionalityReducer,
    viewLeadDisplay: persistedViewLeadDisplayReducer,
    archievedLeadCard: archievedLeadCardSlice,
    duplicateLeadCard: duplicateLeadCardSlice,
    branchMismatchLeadCard: branchMismatchLeadCardSlice,
    leadFunctional: persistedLeadFunctionalReducer,
    leadFilterSlice: leadFilterSlice,
    childFunctional: childFuncionalReducer,
    universityFunctional: universityFunctionalReducer,
    universityFilter: universityFilterReducer,
    courseFunctional: courseFunctionalReducer,
    courseFilter: courseFilterReducer,
    scholarshipFunctional: scholarshipFunctionalReducer,
    dynamicCardFunctionality: persistedDynamicCardFunctionalityReducer,
    dynamicFunctional: persistedDynamicFunctionalReducer,
    dynamicFilter: dynamicFilterReducer,
    settings: settingsReducer,
    automationFunctional: automationFunctionalReducer,

    //niche slices
    educationSlice: educationSlice,
    videoProductionFunctional: videoProductionFunctionalReducer,
    academyFunctional: academyFunctionalReducer,
    // productsFunctional: productsFunctionalReducer,
    // catalogueFunctional: catalogueFunctionalReducer,
    followUpNotification: followUpNotificationReducer,
    installmentNotification: installmentNotificationReducer,
    studentPortal: studentPortalReducer,

    //whatsapp
    whatsapp: whatsappReducer,

    //gmail
    gmail: gmailReducer,

    callLog: callLogReducer,

    // settings
    managePreferences: persistedManagePreferencesReducer,

    // tracks management
    tracksManagement: tracksManagementReducer,

    // requirements
    requirementFunctional: requirementFunctionalReducer,

    // notifications
    notifications: notificationReducer,

    // AI assistant (Cursor-style chat panel)
    aiChat: aiChatReducer,

    // Dashboard v2 filters + detail view (in Redux so they survive routing)
    dashboardV2: dashboardV2Reducer,

    // Reports v2 — global filter bar AND per-card widget state, in Redux for
    // the same reason (the provider unmounts on every navigation), plus the
    // isolation the widget bag buys: one card's key never re-renders another.
    reportsV2: reportsV2Reducer,

    // UI density (root font-size scale). DB-backed per user, not persisted
    // to localStorage — see uiDensitySlice.
    uiDensity: uiDensityReducer,
  });

/**
 * Logout-time hard reset: when `auth/logout` fires, drop every slice's
 * state so the next user starts clean. Each slice falls back to its
 * initial state via `appReducer(undefined, action)`. Without this, only
 * `auth` resets and the other ~50 slices keep prior tenant filters,
 * card state, etc — which then bleed into the next session.
 */
type RootReducerState = ReturnType<typeof appReducer>;
const rootReducer = (
  state: RootReducerState | undefined,
  action: { type: string },
) => {
  if (action.type === "auth/logout") {
    state = undefined;
  }
  return appReducer(state, action as any);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).prepend(viewPrefSyncMiddleware.middleware),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type { StudentPortalState };
export type { TracksManagementState } from "@/features/stage/stores/tracksManagementSlice";
export type { WhatsappState } from "@/features/whatsapp/stores/whatsappSlice";
export type { GmailState } from "@/features/gmail/stores/gmailSlice";
